/**
 * ListCell — shared chrome for "list"-like default column cells.
 *
 * `column.event-list` (events), `column.sticker` (stickers), and
 * `column.task` (tasks) all paint their column's items as a vertical
 * `EntryList` card; this module owns the single make/bind pair so every
 * list-like renderer stays visually identical and new item types only
 * supply a `ListItem[]` viewmodel + an `EntryDisplay`.
 */

import { EntryList } from "@tento-ui/components/entry-list/entry-list";
import type {
  EntryDisplay,
  ListItem,
} from "@tento-ui/components/entry-list/list-item";

/** Builds the entry list a list renderer shows in a cell. */
export namespace ListCell {
  export function make(): EntryList {
    const $l = EntryList.make();
    $l.toggleAttribute("card", true);
    $l.toggleAttribute("no-bg", true);
    $l.style.marginBlock = "1px";
    $l.style.marginInline = "3px";
    $l.style.width = "calc(100% - 6px)";
    $l.style.height = "calc(100% - 2px)";
    $l.style.minHeight = "calc(100% - 2px)";
    return $l;
  }

  /**
   * Paint the card surface (`--bg-surface-light`) on a list cell host. The
   * `#bg` Round is suppressed by `no-bg`, so render the surface directly.
   * Applied only where the cell isn't already on a backed surface — e.g.
   * the sticker list; tasks/events sit in an already-backed context, where
   * a surface would double up.
   */
  export function applySurface($l: EntryList): void {
    $l.style.background = "var(--bg-surface-light)";
    $l.style.borderRadius = "8px";
    $l.style.overflow = "hidden";
  }

  export function bind(
    $cell: EntryList,
    items: ListItem[],
    display: EntryDisplay,
  ): void {
    $cell.assignStyles({
      "--entry-list-bg-color": "var(--bg-surface-light)",
    });
    $cell.bind({ items, display });
  }
}
